import { create } from 'zustand';
import { AuthState } from '../types';
import { authApi } from '../api/auth';
import toast from 'react-hot-toast';

interface LoginCredentials {
  username: string;
  password: string;
  mfaToken?: string;
  rememberMe?: boolean;
}

interface AuthStore extends AuthState {
  login: (credentials: LoginCredentials) => Promise<{ requiresMFA?: boolean; mfaChallenge?: any }>;
  logout: () => Promise<void>;
  initializeAuth: () => void;
  setLoading: (loading: boolean) => void;
  // MFA state
  requiresMFA: boolean;
  mfaChallenge: any;
  pendingCredentials: LoginCredentials | null;
  setMFARequired: (required: boolean, challenge?: any) => void;
  resendMFACode: (username: string) => Promise<void>;
  clearAuth: () => void;
}

export const useAuthStore = create<AuthStore>()(
  (set, get) => ({
    user: null,
    token: null,
    isAuthenticated: false,
    isLoading: false,
    requiresMFA: false,
    mfaChallenge: null,
    pendingCredentials: null,

    setLoading: (loading: boolean) => {
      set({ isLoading: loading });
    },

    setMFARequired: (required: boolean, challenge?: any) => {
      set({ 
        requiresMFA: required, 
        mfaChallenge: challenge,
        isLoading: false 
      });
    },

    clearAuth: () => {
      set({
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        requiresMFA: false,
        mfaChallenge: null,
        pendingCredentials: null,
      });
    },

    login: async (credentials: LoginCredentials) => {
      try {
        set({ isLoading: true, requiresMFA: false, mfaChallenge: null, pendingCredentials: credentials });
        
        const response = await authApi.login(credentials);
        
        if (response.success && response.data) {
          // Check if MFA is required
          if (response.data.requiresMFA) {
            set({
              isLoading: false,
              requiresMFA: true,
              mfaChallenge: response.data.mfaChallenge,
            });
            
            return { 
              requiresMFA: true, 
              mfaChallenge: response.data.mfaChallenge 
            };
          }

          // Complete login
          const { user } = response.data;
          
          set({
            user,
            token: null, // Token is now in HTTP-only cookie
            isAuthenticated: true,
            isLoading: false,
            requiresMFA: false,
            mfaChallenge: null,
            pendingCredentials: null,
          });
          
          toast.success(`Welcome back, ${user.username}!`);
          return {};
        } else {
          throw new Error(response.error?.message || 'Login failed');
        }
      } catch (error: any) {
        set({ 
          isLoading: false, 
          requiresMFA: false, 
          mfaChallenge: null,
          pendingCredentials: null
        });
        const message = error?.response?.data?.error?.message || error.message || 'Login failed';
        toast.error(message);
        throw error;
      }
    },

    logout: async () => {
      try {
        // Call logout API to revoke session and clear cookie
        await authApi.logout();
      } catch (error) {
        // Continue with logout even if API call fails
        console.error('Logout API error:', error);
      }
      
      // Reset auth state
      set({
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        requiresMFA: false,
        mfaChallenge: null,
        pendingCredentials: null,
      });

      toast.success('Logged out successfully');
    },

    initializeAuth: () => {
      const state = get();
      
      // Prevent multiple simultaneous calls
      if (state.isLoading) {
        return;
      }
      
      // Set loading state to true while checking authentication
      set({ isLoading: true });
      
      // Check if user is authenticated by calling the backend
      // The backend will check the HTTP-only cookie automatically
      authApi.getCurrentUser()
        .then((response) => {
          if (response.success && response.data?.user) {
            set({
              user: response.data.user,
              token: null, // Token is now in HTTP-only cookie, not in state
              isAuthenticated: true,
              isLoading: false,
            });
          } else {
            // No valid session
            set({
              user: null,
              token: null,
              isAuthenticated: false,
              isLoading: false,
            });
          }
        })
        .catch(() => {
          // No valid session
          set({
            user: null,
            token: null,
            isAuthenticated: false,
            isLoading: false,
          });
        });
    },

    resendMFACode: async (username: string) => {
      try {
        set({ isLoading: true });
        
        const response = await authApi.resendMFACode(username);
        
        if (response.success) {
          toast.success('Verification code sent successfully');
        } else {
          throw new Error(response.error?.message || 'Failed to resend code');
        }
      } catch (error: any) {
        toast.error(error.message || 'Failed to resend verification code');
        throw error;
      } finally {
        set({ isLoading: false });
      }
    },
  })
);